import React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';

import SidebarEditFormAbsent from '../Form/Absent';
import SidebarEditFormFieldRecordRef from '../Form/FieldRecordRef';
import SidebarEditFormStaticText from '../Form/StaticText';
import SidebarEditFormStaticAttachment from '../Form/StaticAttachment';
import SidebarEditFormFieldText from '../Form/FieldText';
import SidebarEditFormFieldSelect from '../Form/FieldSelect';
import SidebarEditFormFieldMultiAttachment from '../Form/FieldMultiAttachment';
import SidebarEditFormFieldLookup from '../Form/FieldLookup';
import SidebarEditFormFieldNull from '../Form/FieldNull';
import Header from './Header';

import { IRootState } from '../../../modules/stores';
import { ElementTypes } from '../../../types/element';
import { isAbsentField, getForeignTableIdByType } from '../../../helpers';
import { getTableColumnKey } from '../../../modules/space/helper';
import { isDisabledOrDeletedSyncTable } from '../../../modules/core/helpers';

type IConnectProps = ReturnType<typeof mapState>;
interface IProps extends IConnectProps {
  spaceId: string;
}

const SidebarEdit: React.FC<IProps> = ({
  fieldById,
  indexDepth,
  visibleNodes,
  space,
  fieldAbsent,
}) => {
  const getFieldId = () => {
    // find the id of the current field
    // could be a record ref or a lookup pointing to a record ref

    let fieldId;
    if (indexDepth.length === 0) {
      fieldId = space.fieldId;
    } else {
      fieldId = get(space, `${getTableColumnKey(indexDepth)}.id`);
    }

    return fieldId;
  };

  return (
    <>
      <Header spaceType={space.type} fieldId={space.fieldId} fieldAbsent={fieldAbsent} />
      {(() => {
        const commonProps = { space };
        if (fieldAbsent) return <SidebarEditFormAbsent {...commonProps} />;

        switch (ElementTypes[space.type]) {
          case ElementTypes.StaticText:
            return <SidebarEditFormStaticText {...commonProps} />;
          case ElementTypes.StaticAttachment:
            return <SidebarEditFormStaticAttachment {...commonProps} />;
          case ElementTypes.FieldText:
          case ElementTypes.FieldLongText:
          case ElementTypes.FieldPhone:
          case ElementTypes.FieldEmail:
          case ElementTypes.FieldCurrency:
          case ElementTypes.FieldCollaborator:
          case ElementTypes.FieldNumber:
          case ElementTypes.FieldDateTime:
          case ElementTypes.FieldAutoNumber:
          case ElementTypes.FieldFormula:
          case ElementTypes.FieldRollup:
            return <SidebarEditFormFieldText {...commonProps} />;
          case ElementTypes.FieldStatus:
          case ElementTypes.FieldSelect:
          case ElementTypes.FieldMultiSelect:
          case ElementTypes.FieldRating:
          case ElementTypes.FieldCheckbox:
            return <SidebarEditFormFieldSelect {...commonProps} />;
          case ElementTypes.FieldMultiAttachment:
            return <SidebarEditFormFieldMultiAttachment {...commonProps} />;
          case ElementTypes.FieldSubtable:
          case ElementTypes.FieldRecordRef: {
            const fieldId = getFieldId();
            const field = fieldById[fieldId];
            const tableId = getForeignTableIdByType(field);
            const node = visibleNodes[tableId];

            if (isDisabledOrDeletedSyncTable(node.syncTableMeta))
              return <SidebarEditFormFieldNull {...commonProps} />;

            return <SidebarEditFormFieldRecordRef {...commonProps} isNodeVisible={!!node} />;
          }
          case ElementTypes.FieldLookup: {
            const fieldId = getFieldId();
            const field = fieldById[fieldId];
            const tableId = getForeignTableIdByType(field);
            const node = visibleNodes[tableId];

            if (node && isDisabledOrDeletedSyncTable(node.syncTableMeta))
              return <SidebarEditFormFieldNull {...commonProps} />;

            return <SidebarEditFormFieldLookup {...commonProps} isNodeVisible={!!node} />;
          }
          default:
            return <SidebarEditFormFieldNull {...commonProps} />;
        }
      })()}
    </>
  );
};

const mapState = ({ core, space }: IRootState, { spaceId }: { spaceId: string }) => {
  const currentSpace = space.byId[spaceId];

  return {
    visibleNodes: core.visibleNodes,
    fieldById: core.fieldById,
    indexDepth: space.indexDepth,
    space: currentSpace,
    fieldAbsent: isAbsentField(currentSpace, get(core, `fieldById[${currentSpace.fieldId}]`)),
  };
};

export default connect(mapState)(SidebarEdit);
